import type { Dispatch, SetStateAction } from 'react'
import { Provider } from '~/entities/provider'
import { FormControl, FormControlLabel, FormLabel, Radio, RadioGroup } from '@mui/material'

import type { CareTeamRole, SelectedCareTeam } from './types'

interface ProviderDataInterface extends Provider {
  displayName: string
  canProvideCare: boolean
}

interface PropsInterface {
  roleName: CareTeamRole
  selectedCareTeam: SelectedCareTeam
  options: ProviderDataInterface[]
  disabled?: boolean
  setSelectedCareTeam: Dispatch<SetStateAction<SelectedCareTeam>>
  setShowCancel: Dispatch<SetStateAction<boolean>>
  title?: string
}

const ProviderSelectorRadioButtonsGroup = (props: PropsInterface) => {
  const {
    roleName,
    selectedCareTeam,
    options,
    disabled,
    setSelectedCareTeam,
    setShowCancel,
    title,
  } = props

  const unselectableProviderReason = (
    provider: ProviderDataInterface | undefined,
  ): string | null => {
    if (!provider) return 'No provider selected'
    if (!provider.canProvideCare) return `${provider.displayName} is not in-state`
    return null
  }

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const providerId = parseInt((event.target as HTMLInputElement).value)
    // Allow deselecting
    // If the provider is already the choice for the option, unset it
    if (selectedCareTeam[roleName] == providerId) {
      setSelectedCareTeam(current => ({ ...current, [roleName]: null }))
    } else {
      const provider = options.find(option => option.id === providerId)
      if (unselectableProviderReason(provider)) return
      setSelectedCareTeam(current => ({ ...current, [roleName]: providerId }))
    }

    setShowCancel(true)
  }

  return (
    <FormControl component="fieldset">
      {title && (
        <FormLabel color="primary" component="legend">
          {title}
        </FormLabel>
      )}
      <RadioGroup value={selectedCareTeam[roleName]} onClick={handleClick}>
        {options?.map(provider => {
          let label = provider.displayName
          // We still allow selecting a provider marked as not accepting new patients
          // so that we don't block any clinical workflows
          // But, we should show a hint here to help with load-balancing when
          // our team does have to do manual care team selection
          if (provider.provider_fields?.is_taking_new_patients == false) {
            label += ' (Closed)'
          }

          return (
            <FormControlLabel
              key={provider.id}
              value={provider.id}
              control={<Radio />}
              label={label}
              disabled={
                selectedCareTeam[roleName] != provider.id && // always allow de-selecting the chosen provider
                (disabled || !!unselectableProviderReason(provider))
              }
              title={unselectableProviderReason(provider) || ''}
            />
          )
        })}
      </RadioGroup>
    </FormControl>
  )
}

export default ProviderSelectorRadioButtonsGroup
